import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import pieces from '../data/pieces';

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const piece = pieces.find(p => p.id === parseInt(id));
  const fromCollection = location.state?.fromCollection;

  // Si on vient de la collection : on part du noir et on révèle
  // Si accès direct : animation classique
  const [phase, setPhase] = useState(0);
  const [blackOverlay, setBlackOverlay] = useState(fromCollection);

  useEffect(() => {
    if (fromCollection) {
      // Démarre avec tout visible mais recouvert de noir, puis révèle
      setPhase(2);
      setBlackOverlay(true);
      const t1 = setTimeout(() => setBlackOverlay(false), 100);
      return () => clearTimeout(t1);
    }
    setPhase(0);
    const t1 = setTimeout(() => setPhase(1), 50);
    const t2 = setTimeout(() => setPhase(2), 800);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [id, fromCollection]);

  if (!piece) {
    return (
      <div className="h-screen flex items-center justify-center bg-black">
        <div className="text-center">
          <p className="font-serif text-2xl text-white mb-4">Pièce introuvable</p>
          <Link to="/collection" className="text-sm text-white/40 hover:text-white transition-colors underline">
            Retour à la collection
          </Link>
        </div>
      </div>
    );
  }

  const prevId = piece.id > 1 ? piece.id - 1 : null;
  const nextId = piece.id < pieces.length ? piece.id + 1 : null;

  return (
    <div className="h-screen overflow-hidden bg-black relative">


      {/* ── IMAGE PLEIN ECRAN ── */}
      <img
        src={piece.img}
        alt={piece.name}
        className="absolute inset-0 w-full h-full object-cover transition-all duration-[1.5s] ease-out"
        style={{
          transform: phase >= 1 ? 'scale(1)' : 'scale(1.15)',
          opacity: phase >= 1 ? 1 : 0,
          filter: 'brightness(0.4) contrast(1.1) saturate(0.85)',
        }}
      />

      {/* Vignette */}
      <div className="absolute inset-0" style={{ boxShadow: 'inset 0 0 200px 80px rgba(0,0,0,0.5)' }} />

      {/* ── BOUTON RETOUR — haut gauche ── */}
      <button
        onClick={() => navigate('/collection')}
        className={`absolute top-24 left-8 md:left-12 flex items-center gap-3 text-white/50 hover:text-white transition-all z-10 group duration-700 ${phase >= 2 ? 'opacity-100' : 'opacity-0'}`}>
        <span className="w-8 h-px bg-current transition-all duration-300 group-hover:w-14" />
        <span className="text-[11px] tracking-[0.2em] uppercase">Retour</span>
      </button>

      {/* ── NOM — grand, positionné en haut ── */}
      <div className={`absolute top-1/4 left-8 md:left-12 lg:left-16 z-10 transition-all duration-[1.2s] ease-out ${phase >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-px bg-white/30" />
          <p className="text-[10px] tracking-[0.5em] uppercase text-white/40">{piece.type}</p>
        </div>
        <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl text-white font-light leading-none tracking-wide">
          {piece.name}
        </h1>
      </div>

      {/* ── HISTOIRE — centre gauche ── */}
      <div className={`absolute top-1/2 left-8 md:left-12 lg:left-16 -translate-y-1/2 z-10 max-w-md transition-all duration-[1.2s] delay-200 ease-out ${phase >= 2 ? 'opacity-100 translate-y-[-50%]' : 'opacity-0 translate-y-[-40%]'}`}
        style={{ marginTop: '60px' }}>
        <p className="text-white/50 text-[15px] leading-[1.9] font-light">
          {piece.story}
        </p>
      </div>

      {/* ── DETAILS PIERRE — bas gauche ── */}
      <div className={`absolute bottom-28 left-8 md:left-12 lg:left-16 z-10 transition-all duration-[1.2s] delay-400 ease-out ${phase >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
        <div className="flex gap-10">
          <div>
            <p className="text-[9px] tracking-[0.3em] uppercase text-white/25 mb-1">Pierre</p>
            <p className="font-serif text-lg text-white/80">{piece.stone}</p>
          </div>
          <div>
            <p className="text-[9px] tracking-[0.3em] uppercase text-white/25 mb-1">Origine</p>
            <p className="font-serif text-lg text-white/80">{piece.origin}</p>
          </div>
          <div>
            <p className="text-[9px] tracking-[0.3em] uppercase text-white/25 mb-1">Statut</p>
            <p className="font-serif text-lg text-white/80 capitalize">{piece.status}</p>
          </div>
        </div>
      </div>

      {/* ── CTA — bas gauche sous les détails ── */}
      <div className={`absolute bottom-10 left-8 md:left-12 lg:left-16 z-10 transition-all duration-[1.2s] delay-500 ease-out ${phase >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
        <Link to={`/contact?piece=${encodeURIComponent(piece.name)}`}
          className="inline-block px-8 py-3 border border-white/20 text-white/70 text-[11px] tracking-[0.25em] uppercase hover:bg-white hover:text-black transition-all duration-500">
          Me renseigner
        </Link>
      </div>

      {/* ── NAVIGATION PREV/NEXT — bas droite ── */}
      <div className={`absolute bottom-10 right-8 md:right-12 lg:right-16 z-10 flex items-center gap-8 transition-all duration-[1.2s] delay-500 ease-out ${phase >= 2 ? 'opacity-100' : 'opacity-0'}`}>
        {prevId && (
          <Link to={`/collection/${prevId}`}
            onClick={() => setPhase(0)}
            className="text-[11px] tracking-[0.2em] uppercase text-white/30 hover:text-white transition-colors flex items-center gap-3 group">
            <span className="w-6 h-px bg-current transition-all duration-300 group-hover:w-12" />
            Préc.
          </Link>
        )}
        {nextId && (
          <Link to={`/collection/${nextId}`}
            onClick={() => setPhase(0)}
            className="text-[11px] tracking-[0.2em] uppercase text-white/30 hover:text-white transition-colors flex items-center gap-3 group">
            Suiv.
            <span className="w-6 h-px bg-current transition-all duration-300 group-hover:w-12" />
          </Link>
        )}
      </div>

      {/* ── NUMÉRO DE PIÈCE — bas centre ── */}
      <div className={`absolute bottom-10 left-1/2 -translate-x-1/2 z-10 transition-all duration-[1.2s] delay-300 ease-out ${phase >= 2 ? 'opacity-100' : 'opacity-0'}`}>
        <p className="text-[10px] tracking-[0.4em] text-white/15 uppercase">
          {String(piece.id).padStart(2, '0')} / {String(pieces.length).padStart(2, '0')}
        </p>
      </div>
    </div>
  );
}
